/**
 * @module Experience/repdash
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

// core libraries
import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  HostListener,
  ElementRef,
} from '@angular/core';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { Wove } from 'aspect.js/';
// Interfaces
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { CustomerAccountBillingDetails } from '@support/api/proxies/responses/api-2006.interface';
import { KeyValueMap } from '@support/shared/interfaces/key-value-map.interface';
// Services
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { AccountLandingService } from '@experience/landing/common/components/account-landing/services/account-landing.service';
// Constants
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { APP_ROUTE_CONSTANTS } from '@root/shared/constants/app-route.constants';
import { BILLING_CONSTANTS } from '@experience/billing-landing/components/billing-landing.constants';
import { Router } from '@angular/router';
import { PaymentDisclaimerModalComponent } from '@support/payment-disclaimer-modal/components/payment-disclaimer-modal.component';
import { UserService } from '@support/shared/services/user.service';
import { Line } from '../account-landing/interfaces/account-lines-device-details.interface';
import { AppConfig } from '@root/app.config';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Wove({ moduleName: BILLING_CONSTANTS.BILLING_LANDING_MODULE })
@Component({
  selector: 'tmo-billing-block',
  templateUrl: './billing-block.component.html',
  styleUrls: ['./billing-block.component.scss'],
})
export class BillingBlockComponent implements OnInit, OnDestroy {
  /**
   * Variable for Aem data
   */
  public contentData: ConfigData;

  /**
   * Holds boolean if plan is of type tourist
   */
  @Input() public isTouristPlan: boolean;
  /**
   * Variable for emitting boolean event
   */
  @Output() public openAccordion: EventEmitter<boolean>;
  /**
   * string variable having renewal date value
   */
  public renewalDateText: string;
  /**
   * Used to hold boolean value to check whether autopay is enabled or not
   */
  public isAutopayEnabled: boolean;
  /**
   * Used to hold boolean value to check whether billing type is prepaid or postpaid
   */
  public accountBillingTypePrepaid: boolean;
  /**
   * Used to hold boolean value to check whether PastDueAmount is greater than 0
   */
  public showPastDueAmount: boolean;
  /**
   * Used to hold boolean value to check whether EipBalance is greater than 0
   */
  public showEipBalance: boolean;
  /**
   * Used to hold boolean value to check whether we are on billingPage
   */
  @Input() public billingPage?: boolean;
  /**
   * Used to hold the API_2006 response
   */
  @Input() public customerAccountBillingDetails: CustomerAccountBillingDetails;

  @Input() public isBlackout?: boolean;
  /**
   * Stores value of billing type
   */
  public billingType: string;
  /**
   * Stores value of billing details
   */
  public billingDetailsBlock: Object;
  /**
   * Stores value of remaining days text from account landing content
   */
  public dueDateText: string;
  /**
   * Stores value of payment due text from account landing content
   */
  public latePaymentInfo: string;
  /**
   * stores constant value of Permission Constant
   */
  public permissionConstants: KeyValueMap;
  /**
   * To get the radix value
   */
  public RADIX_VALUE_TEN: number;
  /**
   * Holds modal heading
   */
  public modalHeading: string;
  /**
   * Holds modal text
   */
  public modalText: string;
  /**
   * Holds route Id
   */
  public routeId: string;
  /**
   * Holds boolean account is closed
   */
  public isAccountClosed: boolean;
  /**
   * Holds boolean if account is cancelled
   */
  public isCancelledAccount: boolean;
  /**
   * Holds msisdn status
   */
  public msisdnStatus: string;
  public ngUnsubscribe: Subject<void>;

  /**
   * Holds msisdn status
   */
  public isSuspendedAccount: boolean;
  public openModal: boolean;
  public autopayRenewal: string;
  @Input() public isP10MCustomer: boolean;
  @Input() public showAddALine: boolean;
  /**
   * Reference element to get the tooltip
   */
  @ViewChild('toolTip', { static: false }) toolTip: ElementRef;
  /**
   * Allows access to child component - Payment Disclaimer Modal
   */
  @ViewChild('paymentDisclaimerModal', { static: true })
  public paymentDisclaimerModal: PaymentDisclaimerModalComponent;
  /**
   * stores constant value of Billing landing Constant
   */
  public billingLandingConstants: KeyValueMap;
  public billingConstants: KeyValueMap;
  public isGoldMember: string;
  public balanceExpirationDate: string;
  /**
   * stores line sub status value
   */
  public subStatus: string;
  public accountLandingEnhancementsEnabled: boolean;
  public autopayCardNumber: string;
  public autopayCardType: string;
  @Input() public hideRefillCta: boolean;
  @Input() public hideAutopay: boolean;
  @Input() public hideQuickLinks: boolean;
  @Input() private clearAutoPay: boolean;

  constructor(
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private commonUtilService: CommonUtilService,
    private objectService: ObjectService,
    private router: Router,
    private stateStoreUtilsService: StateStoreUtilsService,
    private accountLandingService: AccountLandingService,
    private userService: UserService,
    private appConfig: AppConfig
  ) {
    this.renewalDateText = '';
    this.openAccordion = new EventEmitter();
    const RADIX_VALUE_TEN = 10;
    this.RADIX_VALUE_TEN = RADIX_VALUE_TEN;
    this.isBlackout = false;
    this.openModal = false;
    this.ngUnsubscribe = new Subject<void>();
    this.isSuspendedAccount = true;
  }
  /**
   * Initializing renewal text from the info object
   */
  public ngOnInit(): void {
    this.loadAEMContentData();
    this.permissionConstants = PERMISSION_CONSTANTS;
    this.billingLandingConstants = APP_ROUTE_CONSTANTS;
    this.billingConstants = BILLING_CONSTANTS;
    this.accountBillingTypePrepaid =
      this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus().toLocaleLowerCase() ===
      BILLING_CONSTANTS.PREPAID;
    if (this.clearAutoPay) {
      this.stateStoreUtilsService.clearAutopayModelDetails();
      this.stateStoreUtilsService.clearAutoPaySubmitDetails();
    }
    this.isGoldMember = this.customerAccountBillingDetails.isGoldSubscriber
      ? BILLING_CONSTANTS.ACTIVE
      : '';
    this.accountLandingEnhancementsEnabled = true;

    if (this.customerAccountBillingDetails.balanceExpirationDate) {
      this.balanceExpirationDate = this.datePipe.transform(
        this.customerAccountBillingDetails.balanceExpirationDate,
        'MM/dd/yyyy'
      );
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  public onKeydownHandler(event: KeyboardEvent): void {
    if (this.toolTip && event.key == 'Escape') {
      this.toolTip.nativeElement.blur();
    }
  }
  public loadAEMContentData(): void {
    this.accountLandingService
      .getContentData('account-landing')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((contentData: ConfigData) => {
        this.contentData = contentData;
        if (this.contentData) {
          const primaryLineStatus = this.objectService
            .get(this.customerAccountBillingDetails, 'lines')
            .filter((data: Line) => data.primaryLineIndicator === 'Primary');
          this.getSubStatus(primaryLineStatus[0]);
          if (this.accountBillingTypePrepaid) {
            this.isAutopayEnabled = this.customerAccountBillingDetails.autoPay;
            if (this.accountLandingEnhancementsEnabled) {
              const renewalAmount = Number(this.customerAccountBillingDetails.renewalAmount || 0);
              const availableFunds = Number(this.customerAccountBillingDetails.availableFunds || 0);
              const renewalDate = this.datePipe.transform(
                this.customerAccountBillingDetails.nextBillCycleStartDate,
                this.contentData.renewalAmountTextDateFormat ||
                  <string>this.billingConstants.DATE_FORMAT
              );
              const currentBillCycleEndDate = this.datePipe.transform(
                this.customerAccountBillingDetails.billCycleEndDate,
                this.contentData.renewalAmountTextBillCycleEndDateFormat ||
                  <string>this.billingConstants.DATE_FORMAT
              );
              const availableToRenewal = renewalAmount - availableFunds;
              const requiredAdditionalFunds = availableToRenewal < 0 ? 0 : availableToRenewal;
              const hasSufficientFunds: boolean = availableFunds >= renewalAmount;
              const currencyCode: string = <string>this.billingConstants.CURRENCY_CODE;
              const currencyDisplay: string = <string>this.billingConstants.CURRENCY_DISPLAY;
              const messagesParams: any[] = [
                currentBillCycleEndDate,
                this.currencyPipe.transform(renewalAmount, currencyCode, currencyDisplay),
                this.currencyPipe.transform(requiredAdditionalFunds, currencyCode, currencyDisplay),
                this.currencyPipe.transform(availableFunds, currencyCode, currencyDisplay),
                renewalDate,
              ];
              let renewalContent = this.contentData.insufficientFunds;
              if (
                this.subStatus === this.billingConstants.SUBSTATUS_LOST ||
                (this.customerAccountBillingDetails.accountStatus !==
                  this.billingConstants.STATUS_SUSPENDED &&
                  this.subStatus !== this.billingConstants.SUBSTATUS_UNPAID)
              ) {
                this.isSuspendedAccount = false;
                if (this.isAutopayEnabled) {
                  renewalContent = hasSufficientFunds
                    ? this.contentData.renewalContentSufficientFundsEnabledAutopay
                    : this.contentData.insufficientFundsAutoPay;
                } else {
                  renewalContent = hasSufficientFunds
                    ? this.contentData.renewalContentSufficientFundsDisabledAutopay
                    : this.contentData.newRenewalAmountWithoutAutopayText;
                }
              } else if (this.isAutopayEnabled) {
                renewalContent = this.contentData.autopayErrorMessage;
              } else {
                this.isSuspendedAccount = true;
                renewalContent = this.contentData.overdueFunds;
              }
              if (renewalContent) {
                this.renewalDateText = this.commonUtilService.messageFormatter(
                  renewalContent,
                  messagesParams
                );
              }
            } else {
              if (
                this.subStatus === this.billingConstants.SUBSTATUS_LOST ||
                (this.customerAccountBillingDetails.accountStatus !==
                  this.billingConstants.STATUS_SUSPENDED &&
                  this.subStatus !== this.billingConstants.SUBSTATUS_UNPAID)
              ) {
                if (this.isAutopayEnabled) {
                  this.renewalDateText = this.commonUtilService.messageFormatter(
                    this.contentData.renewalAmountText,
                    [
                      this.datePipe.transform(
                        this.customerAccountBillingDetails.nextBillCycleStartDate,
                        <string>this.billingConstants.DATE_FORMAT
                      ),
                    ]
                  );
                } else {
                  this.renewalDateText = this.commonUtilService.messageFormatter(
                    this.contentData.renewalAmountWithoutAutopayText,
                    [
                      this.datePipe.transform(
                        this.customerAccountBillingDetails.nextBillCycleStartDate,
                        <string>this.billingConstants.DATE_FORMAT
                      ),
                    ]
                  );
                }
              } else {
                this.renewalDateText = this.commonUtilService.messageFormatter(
                  this.contentData.suspendedUnpaidText,
                  []
                );
              }
            }
          } else {
            this.showPastDueAmount =
              parseInt(
                this.objectService.get(this.customerAccountBillingDetails, 'pastDueAmount'),
                this.RADIX_VALUE_TEN
              ) > 0;
            this.showEipBalance =
              parseInt(
                this.objectService.get(
                  this.customerAccountBillingDetails,
                  'postpaid.eipPlanInfo.equipmentCreditLineBalance'
                ),
                this.RADIX_VALUE_TEN
              ) > 0;

            this.dueDateText = this.commonUtilService.messageFormatter(
              this.contentData.dueDateText,
              [this.customerAccountBillingDetails.daysTillDueDate.toString()]
            );

            this.latePaymentInfo = this.commonUtilService.messageFormatter(
              this.contentData.latePaymentInfo,
              [this.customerAccountBillingDetails.remainingDays.toString()]
            );
          }
          this.checkAutopayEnabled();
        }
      });
  }

  public checkAutopayEnabled(): void {
    if (this.isAutopayEnabled) {
      this.autopayRenewal = this.customerAccountBillingDetails.nextBillCycleStartDate;
      this.autopayCardType = this.customerAccountBillingDetails.cardType;
      this.autopayCardNumber = this.customerAccountBillingDetails.cardNumber.slice(
        this.appConfig.getConfig('autoPayCardSlicedChars') || BILLING_CONSTANTS.MINUS_SIX
      );
    }
  }
  public getSubStatus(lineDetails: Line): void {
    this.subStatus = this.userService.getLineStatus(lineDetails);
  }
  /**
   * ngOndestroy for the component
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  /**
   * open payment disclaimer modal
   * @param value, textValue
   * @returns void
   */
  public openDisclaimerModal(value: Event, textValue: string): void {
    this.openModal = this.accountLandingService.openDisclaimerModal(
      textValue,
      this.customerAccountBillingDetails,
      this.contentData
    );
    if (this.openModal) {
      this.modalHeading = this.accountLandingService.modalHeading;
      this.modalText = this.accountLandingService.modalText;
      this.isCancelledAccount = this.accountLandingService.isCancelledAccount;
      this.isAccountClosed = this.accountLandingService.isAccountClosed;
      this.routeId = this.accountLandingService.routeId;
      this.paymentDisclaimerModal.openModal();
    } else {
      if (textValue === this.billingConstants.REDEEM_CARD) {
        this.router.navigate(['/refill-account/redeem']);
      } else if (textValue === this.billingConstants.AUTO_PAY) {
        this.router.navigate(['/autopay']);
      }
    }
  }
  /**
   * Navigate to splash page or add session pass page
   * @returns void
   */
  public navigate(value: boolean): void {
    if (value) {
      window.open(this.contentData.buyAdditionalDataUrl, '_blank');
    } else {
      this.router.navigate(['/change-plans-services/session-pass']);
    }
  }

  /**
   * Emit an event
   * @returns void
   */
  public showAccordion(): void {
    this.openAccordion.emit(true);
  }
}
